/** Locale for the instructor's own speech — always a recognition candidate. */
export const BASE_SPEECH_LOCALE = 'en-US';

/**
 * Azure Speech recognition locale for each language taught in MyLanguageBird, keyed by
 * LanguageID from Language.Languages.
 *
 * Azure supports many regional variants per language (22 for Spanish, 18 for Arabic), but
 * language identification "only uses one locale per base language" — a candidate list can't
 * contain both es-ES and es-MX. The variant chosen affects transcription quality only, never
 * which language gets detected, so each entry is the dialect LanguageBird actually teaches.
 *
 * American Sign Language (1) and ESL (3) are deliberately absent: instructors write those
 * lesson notes in English, so English alone is the correct candidate list and language
 * identification is skipped entirely.
 */
export const SPEECH_LOCALE_BY_LANGUAGE_ID: Readonly<Record<number, string>> = {
	2: 'ar-SA', // Arabic — closest to Modern Standard
	4: 'fr-FR', // French
	5: 'de-DE', // German
	6: 'el-GR', // Greek
	7: 'he-IL', // Hebrew
	8: 'hi-IN', // Hindi
	9: 'it-IT', // Italian
	10: 'ja-JP', // Japanese
	11: 'ko-KR', // Korean
	12: 'zh-CN', // Mandarin, Simplified
	13: 'pt-BR', // Portuguese — Brazilian
	14: 'ru-RU', // Russian
	15: 'es-MX' // Spanish — Latin American
};

/**
 * Recognition candidates for a recording made against a lesson in `languageID`: the base locale,
 * plus the taught language when it differs. A single-entry result means plain recognition; two
 * entries turn on language identification so the instructor can switch between them mid-recording.
 */
export function getRecognitionLocales(languageID?: number | null, baseLocale: string = BASE_SPEECH_LOCALE): string[] {
	const taught = languageID == null ? undefined : SPEECH_LOCALE_BY_LANGUAGE_ID[languageID];
	return taught && taught !== baseLocale ? [baseLocale, taught] : [baseLocale];
}
